import React, { useState } from 'react';
import { Link, useNavigate, useLocation } from 'react-router-dom';
import { Menu, X, Home, Upload, List, LogOut, User, Settings, Wrench, HelpCircle, DollarSign, Shield, Gift } from 'lucide-react';
import { useAuth } from '../../contexts/AuthContext';
import { useToast } from '../../contexts/ToastContext';
import { ProfileDropdown } from './ProfileDropdown';
export function WebsiteHeader() {
  const [isMenuOpen, setIsMenuOpen] = useState(false);
  const navigate = useNavigate();
  const location = useLocation();
  const {
    signOut
  } = useAuth();
  const {
    showToast
  } = useToast();
  const handleLogout = async () => {
    setIsMenuOpen(false);
    await signOut();
    navigate('/');
    showToast('Logged out successfully', 'success');
  };
  const isActivePath = (path: string) => location.pathname === path;
  const desktopNavLinks = [{
    path: '/dashboard',
    label: 'Dashboard',
    icon: Home
  }, {
    path: '/upload',
    label: 'Upload',
    icon: Upload
  }, {
    path: '/all-scans',
    label: 'All Scans',
    icon: List
  }, {
    path: '/dashboard/referrals',
    label: 'Referrals',
    icon: Gift
  }];
  const mobileNavLinks = [...desktopNavLinks, {
    path: '/profile',
    label: 'Profile',
    icon: User
  }, {
    path: '/settings',
    label: 'Settings',
    icon: Settings
  }, {
    path: '/premium-tools',
    label: 'Tools',
    icon: Wrench
  }];
  return <header className="sticky top-0 bg-[#050505]/90 backdrop-blur-md border-b border-gray-800 z-50" data-id="element-1200" data-mp="whqahod33qtn">
      {/* Scanning line at top */}
      <div className="absolute top-0 left-0 w-full h-[1px] bg-gradient-to-r from-transparent via-[#FF0000]/50 to-transparent" data-id="element-1201" data-mp="1bbcs1xgde8a4" />

      <div className="max-w-7xl mx-auto px-6 py-4" data-id="element-1202" data-mp="2mscs5f8ylrep">
        <div className="flex items-center justify-between" data-id="element-1203" data-mp="2mr1gyud6phdk">
          {/* Logo */}
          <Link to="/dashboard" className="flex items-center gap-3 group" data-id="element-1204" data-mp="2ht9mptp2hj67">
            <div className="relative w-8 h-8 flex items-center justify-center bg-[#FF0000]/10 border border-[#FF0000]/30 group-hover:border-[#FF0000] transition-colors" data-id="element-1205" data-mp="2ovy0oyjeuevk">
              <Shield className="w-5 h-5 text-[#FF0000]" data-id="element-1206" data-mp="2yvf5ldqu8oqh" />
              {/* Corner accents */}
              <div className="absolute top-0 left-0 w-1 h-1 border-t border-l border-[#FF0000]" data-id="element-1207" data-mp="33u8mlg5c2xve" />
              <div className="absolute bottom-0 right-0 w-1 h-1 border-b border-r border-[#FF0000]" data-id="element-1208" data-mp="266hvvz0p009c" />
            </div>
            <div className="flex flex-col" data-id="element-1209" data-mp="2b51je3uxpk3t">
              <h1 className="text-lg font-black text-white uppercase tracking-tight leading-none group-hover:text-[#FF0000] transition-colors" data-id="element-1210" data-mp="2b5uj4kvh79cg">
                ToxicDetector
              </h1>
              <span className="text-[10px] font-mono text-gray-500 uppercase tracking-widest" data-id="element-1211" data-mp="2kwxohyvfuahj">
                Forensic Analysis Unit
              </span>
            </div>
          </Link>

          {/* Desktop Navigation */}
          <div className="hidden md:flex items-center gap-6" data-id="element-1212" data-mp="1scqpv0m66yid">
            <nav className="flex items-center gap-1" data-id="element-1213" data-mp="9inqzxeo2rir">
              {desktopNavLinks.map(link => {
              const isActive = isActivePath(link.path);
              return <Link key={link.path} to={link.path} className={`relative px-4 py-2 text-xs font-bold uppercase tracking-widest transition-all group overflow-hidden ${isActive ? 'text-[#FF0000]' : 'text-gray-400 hover:text-white'}`} data-id="element-1214" data-mp="i7pgthhhugj">
                    <span className="relative z-10" data-id="element-1215" data-mp="3hy7l0yn5wdyv">{link.label}</span>
                    {isActive && <>
                        <div className="absolute bottom-0 left-0 w-full h-[2px] bg-[#FF0000]" data-id="element-1216" data-mp="38z2lpq3s0gln" />
                        <div className="absolute top-0 left-0 w-full h-full bg-[#FF0000]/5" data-id="element-1217" data-mp="38x9fud0zm0ep" />
                      </>}
                    {!isActive && <div className="absolute bottom-0 left-0 w-0 h-[1px] bg-gray-600 group-hover:w-full transition-all duration-300" data-id="element-1218" data-mp="2z1of0i7nn66j" />}
                  </Link>;
            })}
            </nav>

            {/* Divider */}
            <div className="h-8 w-px bg-gray-800 mx-2" data-id="element-1219" data-mp="t8qeq9bseovy"></div>

            {/* Profile Dropdown */}
            <ProfileDropdown data-id="element-1220" data-mp="y69sl84koznd" />
          </div>

          {/* Mobile Hamburger Menu */}
          <button className="md:hidden p-2 text-gray-400 hover:text-white hover:bg-gray-800 transition-colors border border-transparent hover:border-gray-700" onClick={() => setIsMenuOpen(!isMenuOpen)} aria-label="Toggle menu" data-id="element-1221" data-mp="k6qwjdp1derf">
            {isMenuOpen ? <X size={24} data-id="element-1222" data-mp="f7xgbdid79nk" /> : <Menu size={24} data-id="element-1223" data-mp="f7e144k3mzd8" />}
          </button>
        </div>

        {/* Mobile Menu Dropdown */}
        {isMenuOpen && <nav className="md:hidden mt-4 border-t border-gray-800 pt-4 animate-in slide-in-from-top-2 duration-200 bg-[#0a0a0a] border-b border-gray-800 absolute left-0 right-0 px-6 pb-6 shadow-2xl" data-id="element-1224" data-mp="24nn79t76jw82">
            <div className="space-y-1" data-id="element-1225" data-mp="3u4sg97p28b3d">
              {mobileNavLinks.map(link => {
            const Icon = link.icon;
            const isActive = isActivePath(link.path);
            return <Link key={link.path} to={link.path} onClick={() => setIsMenuOpen(false)} className={`flex items-center gap-3 px-4 py-3 border border-transparent transition-all ${isActive ? 'bg-[#FF0000]/10 text-[#FF0000] border-[#FF0000]/30' : 'text-gray-400 hover:text-white hover:bg-gray-900 hover:border-gray-800'}`} data-id="element-1226" data-mp="7pzdfruouwtb">
                    <Icon size={16} data-id="element-1227" data-mp="lofti9efxzo4" />
                    <span className="text-sm font-bold uppercase tracking-wider" data-id="element-1228" data-mp="lph6ijf0ow8h">
                      {link.label}
                    </span>
                  </Link>;
          })}
            </div>

            <div className="my-4 border-t border-gray-800" data-id="element-1229" data-mp="2nhdg3nhmjw0m"></div>

            <div className="px-4 py-2 flex items-center justify-between text-gray-500 mb-2" data-id="element-1230" data-mp="rd7c1yxuol6r">
              <div className="flex items-center gap-3" data-id="element-1231" data-mp="wb8vikz6umir">
                <DollarSign size={16} data-id="element-1232" data-mp="wdv32km2x20z" />
                <span className="text-xs font-mono uppercase tracking-wider" data-id="element-1233" data-mp="10cuhcvtpab53">
                  Affiliate Program
                </span>
              </div>
              <span className="text-[10px] font-bold bg-gray-900 text-gray-600 px-1.5 py-0.5 border border-gray-800 uppercase" data-id="element-1234" data-mp="15dqgkw33x946">
                SOON
              </span>
            </div>

            <a href="mailto:support@toxicdetector.com" onClick={() => setIsMenuOpen(false)} className="flex items-center gap-3 px-4 py-3 text-gray-400 hover:text-white hover:bg-gray-900 transition-all border border-transparent hover:border-gray-800" data-id="element-1235" data-mp="1f920dstdh6is">
              <HelpCircle size={16} data-id="element-1236" data-mp="1t3ukz5o4dcw8" />
              <span className="text-sm font-bold uppercase tracking-wider" data-id="element-1237" data-mp="1y0urtissmjoe">
                Support Protocol
              </span>
            </a>

            <button onClick={handleLogout} className="flex items-center gap-3 w-full px-4 py-3 mt-2 text-[#FF0000] hover:bg-[#FF0000]/10 transition-all border border-transparent hover:border-[#FF0000]/30" data-id="element-1238" data-mp="286mb2avt36is">
              <LogOut size={16} data-id="element-1239" data-mp="1u5jmnrhc9k4p" />
              <span className="text-sm font-bold uppercase tracking-wider" data-id="element-1240" data-mp="1u6bi92axrhuz">
                Terminate Session
              </span>
            </button>
          </nav>}
      </div>
    </header>;
}